import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { debounce } from 'lodash';

import {
  CatalogDTO,
  FolderType,
  PlatformLocale,
  ProcessResponseDTO,
  useGetTestCatalog,
  useUpdateProcessCatalogItemOrder
} from '@/api/generated';
import CatalogItemCard from '@/components/CatalogItemCard/TestCatalogItem';
import SortableCatalogItemList from '@/components/SortableCatalogItem/SortableCatalogItemList';
import { useGetProcessTests } from '@/hooks/useGetProcessTests';

interface TestCatalogTabProps {
  processId: string;
  process?: ProcessResponseDTO;
  readonly?: boolean;
}

const TestCatalogTab: React.FC<TestCatalogTabProps> = ({ processId, process, readonly }) => {
  const {
    tests: initialTests,
    isLoading,
    error,
    assessmentType,
    refetch: refetchTests
  } = useGetProcessTests(processId);
  const {
    t,
    i18n: { language: currentLocale }
  } = useTranslation('settings');
  const [selectedTests, setSelectedTests] = useState<CatalogDTO[]>([]);

  useEffect(() => {
    if (initialTests) {
      setSelectedTests(initialTests);
    }
  }, [initialTests]);

  const { data: { catalog } = { catalog: [] }, isLoading: isCatalogLoading } = useGetTestCatalog(
    {
      assessmentType,
      locale: currentLocale as PlatformLocale,
      location_slug: process?.location?.slug as string,
      location_type: process?.location?.type as FolderType
    },
    {
      query: {
        enabled: !!process?.location?.slug && !!process?.location?.type
      }
    }
  );

  const { mutate: updateProcessCatalogItemOrder } = useUpdateProcessCatalogItemOrder();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdateOrder = useCallback(
    debounce((processId: string, tests: CatalogDTO[]) => {
      if (readonly) return;
      updateProcessCatalogItemOrder({
        processId,
        data: { catalogItems: tests.map((test: CatalogDTO) => test.catalogId!) }
      });
    }, 300),
    [updateProcessCatalogItemOrder, readonly]
  );

  const handleReorder = useCallback(
    (newTests: CatalogDTO[]) => {
      setSelectedTests(newTests);
      debouncedUpdateOrder(processId, newTests);
    },
    [processId, debouncedUpdateOrder]
  );

  if (isCatalogLoading || isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{t('processSettings.form.tests.loadError', { error })}</Typography>;
  }

  return (
    <Stack gap={2}>
      <Typography variant="h3" mb={2}>
        {t('processSettings.form.tests.title')}
      </Typography>

      {!!selectedTests.length && (
        <>
          <Typography variant="body3" color="grey.800">
            {t('processSettings.form.tests.selected', { count: selectedTests.length })}
          </Typography>
          <SortableCatalogItemList
            value={selectedTests}
            onChange={handleReorder}
            processId={processId}
            onDelete={refetchTests}
          />
        </>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 2
        }}
      >
        {catalog?.map((catalogItem: CatalogDTO) => (
          <CatalogItemCard
            key={catalogItem.catalogId}
            catalogItem={catalogItem}
            isSelected={selectedTests.some(test => test.catalogId === catalogItem.catalogId)}
            processId={processId}
            currentLocale={currentLocale as PlatformLocale}
            onAddSuccess={refetchTests}
            readonly={readonly}
          />
        ))}
      </Box>
    </Stack>
  );
};

export default TestCatalogTab;
